import React from 'react'
import {
  Apple,
  ArrowRight,
  ShoppingBag,
  Wand2,
  BadgeCheck,
  CalendarCheck,
  CheckCircle2,
  Clapperboard,
  DollarSign,
  Dumbbell,
  Flame,
  GraduationCap,
  Home,
  Leaf,
  MapPin,
  Play,
  Shield,
  Shirt,
  Smile,
  Sparkles,
  Tag,
  Trophy,
  Users,
  UtensilsCrossed,
  Video,
} from 'lucide-react'
import { FaFacebook, FaInstagram, FaTiktok, FaXTwitter, FaYoutube } from 'react-icons/fa6'
import {
  DELIVERABLES,
  INCLUDED_LICENSE_DAYS,
  LICENSE_TERMS,
  formatRange,
  type DeliverableId,
  type RateCard,
} from '@adthlete/common/utils/dealPricing'
import type { MarketplaceProfileData, ProfileSection } from './types'

/*
 * Colours are fixed hex values, never the Tailwind palette: the brand app flips its palette for
 * its light theme and the athlete app doesn't, and this page has to look the same in both.
 */
const C = {
  ink: 'text-[#111827]',
  muted: 'text-[#6B7280]',
  card: 'rounded-2xl border border-[#E5E7EB] bg-[#FFFFFF] shadow-[0_1px_2px_rgba(16,24,40,0.04)]',
}

const compact = (n: number) =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })
    .format(n)
    .replace('K', 'K')
const pct = (n: number | null | undefined, digits = 0) =>
  n == null ? '—' : `${(n * 100).toFixed(digits)}%`
const ago = (iso: string | null) => {
  if (!iso) return null
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days < 1) return 'Today'
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`
  if (days < 30) return `${Math.floor(days / 7)} week${days < 14 ? '' : 's'} ago`
  const m = Math.floor(days / 30)
  return `${m} month${m === 1 ? '' : 's'} ago`
}

const PLATFORM: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; bg: string; label: string; unit: string }
> = {
  instagram: {
    icon: FaInstagram,
    bg: 'bg-[linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)]',
    label: 'Instagram',
    unit: 'Followers',
  },
  tiktok: { icon: FaTiktok, bg: 'bg-[#111111]', label: 'TikTok', unit: 'Followers' },
  youtube: { icon: FaYoutube, bg: 'bg-[#FF0000]', label: 'YouTube', unit: 'Subscribers' },
  twitter: { icon: FaXTwitter, bg: 'bg-[#1D9BF0]', label: 'X', unit: 'Followers' },
  x: { icon: FaXTwitter, bg: 'bg-[#1D9BF0]', label: 'X', unit: 'Followers' },
  facebook: { icon: FaFacebook, bg: 'bg-[#1877F2]', label: 'Facebook', unit: 'Followers' },
}
const platformOf = (p: string) =>
  PLATFORM[p] ?? { icon: Video, bg: 'bg-[#6B7280]', label: p, unit: 'Followers' }

const PlatformBadge: React.FC<{ platform: string; size?: 'sm' | 'md' }> = ({
  platform,
  size = 'md',
}) => {
  const p = platformOf(platform)
  const Icon = p.icon
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-lg text-[#FFFFFF] ${p.bg} ${
        size === 'sm' ? 'h-6 w-6' : 'h-9 w-9'
      }`}
    >
      <Icon className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-5 w-5'} />
    </span>
  )
}

const THEME_ICON: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; tint: string }
> = {
  training_fitness: { icon: Dumbbell, tint: 'bg-[#E3EEFD] text-[#2563EB]' },
  recovery_wellness: { icon: Leaf, tint: 'bg-[#E8F9D2] text-[#3F7A0A]' },
  nutrition: { icon: Apple, tint: 'bg-[#E8F9D2] text-[#15803D]' },
  food_dining: { icon: UtensilsCrossed, tint: 'bg-[#FEF3D7] text-[#B45309]' },
  beauty_grooming: { icon: Sparkles, tint: 'bg-[#FCE7F3] text-[#BE185D]' },
  lifestyle: { icon: Home, tint: 'bg-[#EEF2FF] text-[#4F46E5]' },
  sport_competition: { icon: Trophy, tint: 'bg-[#FFEDD5] text-[#EA580C]' },
  fashion_style: { icon: Shirt, tint: 'bg-[#F1E8FD] text-[#7C3AED]' },
  education_career: { icon: GraduationCap, tint: 'bg-[#E0F2FE] text-[#0369A1]' },
  humor_comedy: { icon: Smile, tint: 'bg-[#FEF9C3] text-[#A16207]' },
  entertainment_pop_culture: { icon: Clapperboard, tint: 'bg-[#FCE7F3] text-[#BE185D]' },
  motivation_mindset: { icon: Flame, tint: 'bg-[#FFEDD5] text-[#EA580C]' },
}
const themeIcon = (category: string) =>
  THEME_ICON[category] ?? { icon: Tag, tint: 'bg-[#F3F4F6] text-[#4B5563]' }

const SectionTitle: React.FC<{
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
  right?: React.ReactNode
}> = ({ icon: Icon, children, right }) => (
  <div className="mb-4 flex items-center justify-between gap-3">
    <h2 className={`flex items-center gap-2.5 text-lg font-bold ${C.ink}`}>
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F0FDE4] text-[#3F7A0A]">
        <Icon className="h-4 w-4" />
      </span>
      {children}
    </h2>
    {right}
  </div>
)

/** A two-slice donut for the gender split, drawn with a conic gradient */
const Donut: React.FC<{ split: { label: string; share: number }[] }> = ({ split }) => {
  const top = [...split].sort((a, b) => b.share - a.share)[0]
  const male = split.find((s) => s.label === 'Male')?.share ?? 0
  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="relative h-32 w-32 rounded-full"
        style={{
          background: `conic-gradient(#3B82F6 0 ${male * 360}deg, #F472B6 ${male * 360}deg 360deg)`,
        }}
        role="img"
        aria-label={split.map((s) => `${s.label} ${pct(s.share)}`).join(', ')}
      >
        <div className="absolute inset-3 flex flex-col items-center justify-center rounded-full bg-[#FFFFFF]">
          <span className={`text-2xl font-bold ${C.ink}`}>{pct(top?.share)}</span>
          <span className={`text-xs ${C.muted}`}>{top?.label}</span>
        </div>
      </div>
      <ul className="w-full space-y-1 text-sm">
        {split.map((s) => (
          <li key={s.label} className="flex items-center justify-between gap-6">
            <span className={`flex items-center gap-2 ${C.ink}`}>
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  s.label === 'Male'
                    ? 'bg-[#3B82F6]'
                    : s.label === 'Female'
                      ? 'bg-[#F472B6]'
                      : 'bg-[#9CA3AF]'
                }`}
              />
              {s.label}
            </span>
            <span className={`tabular-nums ${C.muted}`}>{pct(s.share)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const Bar: React.FC<{ share: number; max: number }> = ({ share, max }) => (
  <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#F3F4F6]">
    <div
      className="h-full rounded-full bg-[#A3E635]"
      style={{ width: `${Math.max(3, (share / (max || 1)) * 100)}%` }}
    />
  </div>
)

/**
 * What the Athios Score means to a brand. A 30 is where a new athlete starts, not a warning:
 * the score climbs as they fill in their profile, post, finish deals and learn.
 */
const SCORE_LEGEND =
  'The Athios Score (0 to 100) adds up a complete profile (30), posting in the last 30 days (25), deals delivered on time (15) and Creator Academy lessons (30). New athletes start around 30. L1 is getting started, L2 is ready for deals, L3 has delivered three or more deals here, L4 is a top performer with ten or more.'
const scoreHint = (value: number) =>
  value < 45
    ? 'Just getting started: the score climbs as they fill in their profile, post and deliver.'
    : value < 70
      ? 'Building: posting and delivering deals raise it. 85+ are our top creators.'
      : value < 85
        ? 'Strong: a complete profile, regular posting and deals delivered.'
        : 'Top creator: complete profile, posts steadily, deals delivered on time.'

/** The fit score as a ring */
const FitRing: React.FC<{ score: number; size?: number }> = ({ score, size = 96 }) => {
  const r = (size - 12) / 2
  const c = 2 * Math.PI * r
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={10} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#4D7C0F"
        strokeWidth={10}
        strokeLinecap="round"
        strokeDasharray={`${(score / 100) * c} ${c}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        className="fill-[#111827] text-xl font-bold"
      >
        {score}%
      </text>
    </svg>
  )
}

const PART_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  audience: Users,
  content: Sparkles,
  brand: Trophy,
  location: MapPin,
}

/** Why they fit, at a glance: the score as a ring and its four parts as bars */
const FitBreakdown: React.FC<{
  fit: NonNullable<MarketplaceProfileData['fit']>
}> = ({ fit }) => (
  <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
    <div className="flex items-center gap-4 rounded-xl bg-[#F7FEE7] p-4 md:flex-col md:items-start">
      <FitRing score={fit.score} />
      <div className="min-w-0">
        <p className="text-lg font-bold text-[#3F7A0A]">{fit.label}</p>
        {fit.why ? <p className={`text-sm leading-snug ${C.muted}`}>{fit.why}</p> : null}
      </div>
    </div>
    <ul className="grid gap-3 sm:grid-cols-2">
      {(fit.parts ?? []).map((p) => {
        const Icon = PART_ICON[p.key] ?? Sparkles
        return (
          <li key={p.key} className="flex min-w-0 gap-3 rounded-xl border border-[#E5E7EB] p-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F3F4F6] text-[#111827]">
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className={`text-sm font-semibold ${C.ink}`}>{p.label}</p>
                <p className={`text-sm font-bold tabular-nums ${C.ink}`}>{p.score}%</p>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#E5E7EB]">
                <div
                  className="h-full rounded-full bg-[#4D7C0F]"
                  style={{ width: `${p.score}%` }}
                />
              </div>
              <p className={`mt-1 text-xs ${C.muted}`}>{p.detail}</p>
            </div>
          </li>
        )
      })}
    </ul>
  </div>
)

/** What a brand picked on the storefront: a format in one of the athlete's themes, or custom */
export type BookPick =
  | { kind: 'rate_card'; themeId: string | null; deliverable: DeliverableId }
  | { kind: 'custom'; themeId: string | null }
  /** One of the athlete's own items (rateCard.items) */
  | { kind: 'item'; themeId: null; itemId: string }

/** A pick as a link parameter (?book=…), so it survives signing in: "rate_card.grwm.ig_reel" */
export const encodePick = (p: BookPick) =>
  [
    p.kind,
    p.themeId ?? '_',
    p.kind === 'rate_card' ? p.deliverable : p.kind === 'item' ? p.itemId : '',
  ].join('.')
const DELIVERABLE_IDS: DeliverableId[] = [
  'ig_reel',
  'tiktok_video',
  'ig_story',
  'static_post',
  'appearance',
]
export const decodePick = (raw: string | null | undefined): BookPick | null => {
  const [kind, theme, deliverable] = (raw ?? '').split('.')
  const themeId = theme && theme !== '_' ? theme : null
  if (kind === 'custom') return { kind: 'custom', themeId }
  if (kind === 'item' && /^item-[a-z0-9-]{1,48}$/.test(deliverable ?? ''))
    return { kind: 'item', themeId: null, itemId: deliverable }
  if (kind === 'rate_card' && DELIVERABLE_IDS.includes(deliverable as DeliverableId))
    return { kind: 'rate_card', themeId, deliverable: deliverable as DeliverableId }
  return null
}

// Short names for the format chips on a product
const FORMAT_SHORT: Record<DeliverableId, string> = {
  ig_reel: 'IG Reel',
  tiktok_video: 'TikTok',
  ig_story: 'IG Stories',
  static_post: 'IG Post',
  appearance: 'Appearance',
}
const CONTENT_FORMATS = DELIVERABLES.filter((d) => d.id !== 'appearance')
const offered = (card: RateCard | null, id: DeliverableId) => {
  const e = card?.deliverables[id]
  return e && e.high > 0 ? e : null
}

type Tile = {
  key: string
  link: string
  thumbnail: string | null
  platform: string
  views: number | null
  label: string
  /** The theme it proves, for an athlete's theme example */
  theme: string | null
}

/** One piece of their creative in the mosaic: the thumbnail, platform, views and what it is */
const CreativeTile: React.FC<{ t: Tile; big?: boolean }> = ({ t, big }) => (
  <a
    href={t.link || undefined}
    target="_blank"
    rel="noreferrer"
    className={`group relative block overflow-hidden rounded-2xl bg-[#111827] ${
      big ? 'col-span-2 row-span-2 aspect-[4/5] sm:aspect-auto' : 'aspect-[4/5]'
    }`}
  >
    {t.thumbnail ? (
      <img
        src={t.thumbnail}
        alt=""
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
      />
    ) : null}
    <span className="absolute inset-x-0 bottom-0 h-2/3 bg-[linear-gradient(to_top,rgba(0,0,0,0.75),transparent)]" />
    <span className="absolute left-2.5 top-2.5">
      <PlatformBadge platform={t.platform} size="sm" />
    </span>
    {t.theme ? (
      <span className="absolute right-2.5 top-2.5 max-w-[70%] truncate rounded-full bg-[#C0FD72] px-2 py-0.5 text-[11px] font-bold text-[#1A2E05]">
        {t.theme}
      </span>
    ) : null}
    <span className="absolute inset-x-2.5 bottom-2.5 text-[#FFFFFF]">
      {t.views ? (
        <span className="flex items-center gap-1 text-xs font-semibold">
          <Play className="h-3 w-3 fill-[#FFFFFF]" /> {compact(t.views)} views
        </span>
      ) : null}
      <span className={`line-clamp-2 font-semibold leading-snug ${big ? 'text-base' : 'text-xs'}`}>
        {t.label}
      </span>
    </span>
  </a>
)

/**
 * One thing a brand can buy: a format in one of the athlete's themes. The example post shows
 * what they get; the chips switch the format and its price; Book this opens the offer, filled in.
 */
const ProductCard: React.FC<{
  label: string
  category: string
  sub: string
  example: {
    url: string
    thumbnail: string | null
    platform: string | null
    views: number | null
  } | null
  card: RateCard | null
  first: string
  onBook?: (pick: BookPick) => void
  themeId: string | null
}> = ({ label, category, sub, example, card, first, onBook, themeId }) => {
  const formats = CONTENT_FORMATS.filter((d) => offered(card, d.id))
  const [pick, setPick] = React.useState<DeliverableId | null>(formats[0]?.id ?? null)
  const entry = pick ? offered(card, pick) : null
  const unit = DELIVERABLES.find((d) => d.id === pick)?.unit
  const ic = themeIcon(category)
  const Icon = ic.icon
  return (
    <li className={`${C.card} flex min-w-0 flex-col overflow-hidden`}>
      {/* What you get: their own post in this theme */}
      <a
        href={example?.url || undefined}
        target="_blank"
        rel="noreferrer"
        className={`relative block aspect-[16/11] overflow-hidden ${example?.thumbnail ? 'bg-[#111827]' : ic.tint}`}
      >
        {example?.thumbnail ? (
          <img
            src={example.thumbnail}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center">
            <Icon className="h-10 w-10 opacity-70" />
          </span>
        )}
        {example ? (
          <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-md bg-[rgba(0,0,0,0.65)] px-2 py-0.5 text-xs font-semibold text-[#FFFFFF]">
            <Play className="h-3 w-3 fill-[#FFFFFF]" />
            {example.views ? `${compact(example.views)} views` : `See ${first}'s example`}
          </span>
        ) : null}
      </a>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wider ${C.muted}`}>
            Series · {sub}
          </p>
          <p className={`text-lg font-bold leading-tight ${C.ink}`}>{label}</p>
        </div>
        {formats.length ? (
          <>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Format">
              {formats.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  role="radio"
                  aria-checked={pick === d.id}
                  onClick={() => setPick(d.id)}
                  className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                    pick === d.id
                      ? 'border-[#0F172A] bg-[#0F172A] text-[#FFFFFF]'
                      : 'border-[#E5E7EB] bg-[#FFFFFF] text-[#374151] hover:border-[#9BD85A]'
                  }`}
                >
                  {FORMAT_SHORT[d.id]}
                </button>
              ))}
            </div>
            <div className="mt-auto flex items-end justify-between gap-3">
              <div>
                <p className={`text-2xl font-bold tabular-nums ${C.ink}`}>
                  {entry ? formatRange(entry) : '—'}
                </p>
                <p className={`text-xs ${C.muted}`}>
                  {unit} · {INCLUDED_LICENSE_DAYS} days of ad usage · you approve it before it
                  posts
                </p>
              </div>
            </div>
            {onBook ? (
              <button
                type="button"
                onClick={() => pick && onBook({ kind: 'rate_card', themeId, deliverable: pick })}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#C0FD72] font-semibold text-[#0F172A] hover:bg-[#B2F25E]"
              >
                <ShoppingBag className="h-4 w-4" /> Book this
              </button>
            ) : (
              <span className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-[#F3F4F6] text-sm font-semibold text-[#6B7280]">
                Brands book this here
              </span>
            )}
          </>
        ) : (
          <>
            <p className={`mt-auto text-sm ${C.muted}`}>No set price yet. Ask {first} for one.</p>
            {onBook ? (
              <button
                type="button"
                onClick={() => onBook({ kind: 'custom', themeId })}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#0F172A] font-semibold text-[#0F172A] hover:bg-[#F9FAFB]"
              >
                Request a price <ArrowRight className="h-4 w-4" />
              </button>
            ) : null}
          </>
        )}
      </div>
    </li>
  )
}

/**
 * An athlete's Content Shop in the Marketplace: brands shop their creative, in that order —
 * what they make, what you can book, then the numbers behind it. Their best posts up
 * top; then what you can buy, one product per content theme with their example, the formats
 * and prices, and Book this; then why they fit, their numbers and audience. The trust signals
 * (score, availability, track record) sit in the side column. The athlete app shows the same
 * page as "How brands see you", with notes on what to improve and no booking.
 */
export const MarketplaceProfile: React.FC<{
  data: MarketplaceProfileData
  /** The side column's buttons (Work with, Save to shortlist, Share) */
  actions?: React.ReactNode
  /** Notes shown at the top of a section, e.g. "Verify your numbers" in the athlete's preview */
  notes?: Partial<Record<ProfileSection, React.ReactNode>>
  /** Book a product from the storefront (left out in the athlete's own preview) */
  onBook?: (pick: BookPick) => void
}> = ({ data, actions, notes = {}, onBook }) => {
  const a = data.athlete
  const first = a.name.split(' ')[0]
  const card = data.rateCard
  const maxAge = Math.max(0, ...(data.audience?.ages.map((x) => x.share) ?? [0]))

  // The products: one per content theme, or (with no themes) one for their content in general
  const products = data.themes.length
    ? data.themes.map((t) => ({
        key: t.id,
        themeId: t.id,
        label: t.label,
        sub: t.categoryName,
        category: t.category,
        example: t.example ?? null,
      }))
    : [
        {
          key: 'any',
          themeId: null,
          label: `Content from ${first}`,
          sub: data.contentMix[0]?.name ?? 'Their usual content',
          category: data.contentMix[0]?.category ?? '',
          example: null,
        },
      ]
  const appearance = offered(card, 'appearance')

  // The creative up top: their most-watched posts. The series examples are in the shop below,
  // so they only fill in here when there aren't enough other posts
  const exampleUrls = new Set(data.themes.map((t) => t.example?.url).filter(Boolean) as string[])
  const themeOf = new Map(
    data.themes.filter((t) => t.example?.url).map((t) => [t.example!.url, t.label]),
  )
  const seen = new Set<string>()
  const tiles: Tile[] = []
  for (const f of data.featured) {
    const k = f.link || f.thumbnail
    if (!k || !f.thumbnail || seen.has(k) || exampleUrls.has(f.link)) continue
    seen.add(k)
    tiles.push({
      key: k,
      link: f.link,
      thumbnail: f.thumbnail,
      platform: f.platform,
      views: f.views || null,
      label: f.title || 'Untitled post',
      theme: themeOf.get(f.link) ?? null,
    })
  }
  for (const t of data.themes) {
    const e = t.example
    if (tiles.length >= 5) break
    if (!e?.thumbnail || seen.has(e.url)) continue
    seen.add(e.url)
    tiles.push({
      key: e.url,
      link: e.url,
      thumbnail: e.thumbnail,
      platform: e.platform ?? 'instagram',
      views: e.views,
      label: e.caption || t.label,
      theme: t.label,
    })
  }
  const mosaic = tiles.slice(0, 5)

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px] [&>*]:min-w-0">
      {/* The creative, and whose it is */}
      {/* On a phone the shop comes right after the hero; the trust column follows it */}
      <section className={`${C.card} order-1 p-4 sm:p-5 lg:order-none lg:col-start-1`}>
        {notes.hero}
        <div className="flex flex-wrap items-center gap-3">
          {a.avatar ? (
            <img src={a.avatar} alt="" className="h-14 w-14 shrink-0 rounded-full object-cover" />
          ) : (
            <span
              className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#F3F4F6] text-xl font-bold ${C.muted}`}
            >
              {a.name.charAt(0)}
            </span>
          )}
          <div className="min-w-[11rem] flex-1">
            <p className="text-xs font-bold uppercase tracking-wider text-[#3F7A0A]">
              Content Shop
            </p>
            <h1 className={`flex items-center gap-2 text-2xl font-bold ${C.ink}`}>
              <span className="truncate">{a.name}</span>
              {a.verified ? (
                <BadgeCheck
                  className="h-5 w-5 shrink-0 fill-[#2563EB] text-[#FFFFFF]"
                  aria-label="Verified numbers"
                />
              ) : null}
            </h1>
            <p className={`truncate text-sm ${C.muted}`}>
              {[a.school, a.sports.join(', '), a.location].filter(Boolean).join('  •  ')}
            </p>
          </div>
          {data.fit ? (
            <span className="shrink-0 rounded-full bg-[#C0FD72] px-3 py-1 text-sm font-bold text-[#1A2E05]">
              {data.fit.label} · {data.fit.score}
            </span>
          ) : null}
        </div>

        <div className="mt-4">
          {/* First: what they make. The shop and the numbers follow */}
          <p className={`mb-2 text-sm font-semibold ${C.ink}`}>
            What {first} makes <span className={`font-normal ${C.muted}`}>· tap to watch</span>
          </p>
          {notes.featured}
          {mosaic.length ? (
            <div
              className={`grid grid-cols-2 gap-2.5 ${mosaic.length > 2 ? 'sm:grid-cols-4' : ''}`}
            >
              {mosaic.map((t, i) => (
                <CreativeTile key={t.key} t={t} big={i === 0 && mosaic.length > 2} />
              ))}
            </div>
          ) : (
            <p className={`rounded-xl bg-[#F9FAFB] p-6 text-center text-sm ${C.muted}`}>
              No posts to show yet.
            </p>
          )}
        </div>

        {/* Their numbers, in one line under the creative */}
        {data.channels.length ? (
          <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
            {data.channels.map((ch) => (
              <li key={ch.platform} className="flex items-center gap-2">
                <PlatformBadge platform={ch.platform} size="sm" />
                <span className={`font-bold tabular-nums ${C.ink}`}>{compact(ch.followers)}</span>
                <span className={`text-sm ${C.muted}`}>
                  {platformOf(ch.platform).unit.toLowerCase()} · {pct(ch.engagementRate, 1)} eng.
                </span>
                {ch.verified ? (
                  <BadgeCheck
                    className="h-4 w-4 fill-[#2563EB] text-[#FFFFFF]"
                    aria-label="Verified"
                  />
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-4">{notes.channels}</div>
        )}
        {data.channels.length ? notes.channels : null}
      </section>

      {/* Book, save, share; then why to trust them */}
      <aside className="order-3 space-y-5 lg:order-none lg:col-start-2 lg:row-span-4 lg:row-start-1 lg:self-start">
        {actions ? <div className={`${C.card} space-y-2.5 p-4`}>{actions}</div> : null}

        {/* What makes this a shop, not a profile: the whole deal happens here */}
        <section className="rounded-2xl border border-[#D9F99D] bg-[#F7FEE7] p-4">
          <p className={`font-bold ${C.ink}`}>Why book on Athios</p>
          <ul className="mt-2 space-y-2 text-sm text-[#374151]">
            {[
              'Prices up front, no back-and-forth to get a quote',
              'You review the content before it goes live',
              'Brief, delivery and payment in one place',
              `${INCLUDED_LICENSE_DAYS} days of paid-ad usage included`,
            ].map((t) => (
              <li key={t} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#3F7A0A]" />
                {t}
              </li>
            ))}
          </ul>
        </section>

        <section className={`${C.card} space-y-4 p-4`}>
          <div className="flex items-center gap-3" title={SCORE_LEGEND}>
            <span className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-[#F7FEE7] ring-1 ring-[#D9F99D]">
              <span className={`text-lg font-bold leading-none tabular-nums ${C.ink}`}>
                {data.score.value}
              </span>
              <span className={`text-[10px] ${C.muted}`}>/100</span>
            </span>
            <div className="min-w-0">
              <p className={`font-semibold ${C.ink}`}>
                Athios Score · {data.score.level} {data.score.label}
              </p>
              <p className={`text-xs leading-snug ${C.muted}`}>{scoreHint(data.score.value)}</p>
            </div>
          </div>
          <div className="flex gap-3 border-t border-[#F3F4F6] pt-4">
            <CalendarCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#3F7A0A]" />
            <div>
              <p className={`font-semibold ${C.ink}`}>Availability</p>
              <p className="flex items-center gap-1.5 text-sm text-[#15803D]">
                <span
                  className={`h-2 w-2 rounded-full ${data.availability.openToOffers ? 'bg-[#22C55E]' : 'bg-[#9CA3AF]'}`}
                />
                {data.availability.openToOffers ? 'Open to offers' : 'Not taking offers right now'}
              </p>
            </div>
          </div>
          <div className="flex gap-3 border-t border-[#F3F4F6] pt-4">
            <Trophy className="mt-0.5 h-5 w-5 shrink-0 text-[#EA580C]" />
            <div className="min-w-0">
              <p className={`font-semibold ${C.ink}`}>Track record</p>
              <p className={`text-sm ${C.muted}`}>
                {data.record.completed} delivered · {data.record.inProgress} in progress
              </p>
              {data.record.brands.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {data.record.brands.slice(0, 4).map((b) =>
                    b.logo ? (
                      <img
                        key={b.name}
                        src={b.logo}
                        alt={b.name}
                        title={b.name}
                        className="h-9 w-9 rounded-lg border border-[#E5E7EB] object-cover"
                      />
                    ) : (
                      <span
                        key={b.name}
                        title={b.name}
                        className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F3F4F6] text-xs font-bold text-[#374151]"
                      >
                        {b.name.slice(0, 2).toUpperCase()}
                      </span>
                    ),
                  )}
                  {data.record.brands.length > 4 ? (
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F3F4F6] text-sm font-semibold text-[#374151]">
                      +{data.record.brands.length - 4}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          {card ? (
            <div className="flex gap-3 border-t border-[#F3F4F6] pt-4">
              <Shield className="mt-0.5 h-5 w-5 shrink-0 text-[#3F7A0A]" />
              <div>
                <p className={`font-semibold ${C.ink}`}>Ad usage</p>
                <p className={`text-xs ${C.muted}`}>
                  {INCLUDED_LICENSE_DAYS} days included with everything. Extend for $
                  {card.licensing[30].amount.toLocaleString('en-US')}–
                  {card.licensing[180].amount.toLocaleString('en-US')} per video (
                  {LICENSE_TERMS[0].label} to {LICENSE_TERMS[LICENSE_TERMS.length - 1].label}).
                </p>
              </div>
            </div>
          ) : null}
        </section>
      </aside>

      {/* The shop */}
      <section className="order-2 space-y-3 lg:order-none lg:col-start-1">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className={`flex items-center gap-2 text-xl font-bold ${C.ink}`}>
              <ShoppingBag className="h-5 w-5 text-[#3F7A0A]" /> Shop {first}'s content
            </h2>
            <p className={`text-sm ${C.muted}`}>
              The series {first} already posts, priced and ready to book. Every piece comes with{' '}
              {INCLUDED_LICENSE_DAYS} days of paid-ad usage.
            </p>
          </div>
        </div>
        <ol className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
          {['Pick a series', 'Pick a format', 'Book it'].map((step, i) => (
            <li key={step} className={`flex items-center gap-1.5 font-semibold ${C.ink}`}>
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0F172A] text-[11px] text-[#FFFFFF]">
                {i + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
        {notes.themes}
        {notes.rates}
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {products.map((p) => (
            <ProductCard
              key={p.key}
              label={p.label}
              sub={p.sub}
              category={p.category}
              example={p.example}
              card={card}
              first={first}
              onBook={onBook}
              themeId={p.themeId}
            />
          ))}
          {appearance ? (
            <li className={`${C.card} flex min-w-0 flex-col overflow-hidden`}>
              {/* Same shape as a series card, so the shelf lines up */}
              <span className="relative flex aspect-[16/11] items-center justify-center bg-[linear-gradient(135deg,#FFEDD5,#FED7AA)] text-[#EA580C]">
                <Users className="h-10 w-10 opacity-80" />
                <span className="absolute bottom-2 left-2 rounded-md bg-[rgba(0,0,0,0.65)] px-2 py-0.5 text-xs font-semibold text-[#FFFFFF]">
                  {first}, in person
                </span>
              </span>
              <div className="flex flex-1 flex-col gap-3 p-4">
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wider ${C.muted}`}>
                    In person
                  </p>
                  <p className={`text-lg font-bold leading-tight ${C.ink}`}>Appearance</p>
                  <p className={`text-sm ${C.muted}`}>
                    Your event, store opening, campus pop-up or meet and greet.
                  </p>
                </div>
                <div className="mt-auto">
                  <p className={`text-2xl font-bold tabular-nums ${C.ink}`}>
                    {formatRange(appearance)}
                  </p>
                  <p className={`text-xs ${C.muted}`}>per hour</p>
                </div>
                {onBook ? (
                  <button
                    type="button"
                    onClick={() =>
                      onBook({ kind: 'rate_card', themeId: null, deliverable: 'appearance' })
                    }
                    className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#C0FD72] font-semibold text-[#0F172A] hover:bg-[#B2F25E]"
                  >
                    <ShoppingBag className="h-4 w-4" /> Book this
                  </button>
                ) : null}
              </div>
            </li>
          ) : null}
          {/* Their own items, priced by them */}
          {(card?.items ?? []).map((item) => (
            <li key={item.id} className={`${C.card} flex min-w-0 flex-col overflow-hidden`}>
              <span className="relative flex aspect-[16/11] items-center justify-center bg-[linear-gradient(135deg,#EEF2FF,#E0E7FF)] text-[#4F46E5]">
                <Tag className="h-10 w-10 opacity-80" />
              </span>
              <div className="flex flex-1 flex-col gap-3 p-4">
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wider ${C.muted}`}>
                    From {first}
                  </p>
                  <p className={`text-lg font-bold leading-tight ${C.ink}`}>{item.label}</p>
                  {item.description ? (
                    <p className={`text-sm ${C.muted}`}>{item.description}</p>
                  ) : null}
                </div>
                <div className="mt-auto">
                  <p className={`text-2xl font-bold tabular-nums ${C.ink}`}>{formatRange(item)}</p>
                  <p className={`text-xs ${C.muted}`}>{item.unit}</p>
                </div>
                {onBook ? (
                  <button
                    type="button"
                    onClick={() => onBook({ kind: 'item', themeId: null, itemId: item.id })}
                    className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#C0FD72] font-semibold text-[#0F172A] hover:bg-[#B2F25E]"
                  >
                    <ShoppingBag className="h-4 w-4" /> Book this
                  </button>
                ) : null}
              </div>
            </li>
          ))}
          {onBook ? (
            <li className="flex flex-col gap-3 rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F9FAFB] p-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFFFFF] text-[#4F46E5] ring-1 ring-[#E5E7EB]">
                <Wand2 className="h-5 w-5" />
              </span>
              <div>
                <p className={`text-lg font-bold leading-tight ${C.ink}`}>Something custom</p>
                <p className={`text-sm ${C.muted}`}>
                  An unboxing, a product demo, a series of your own. Describe it and name your
                  price, or ask {first} for theirs.
                </p>
              </div>
              <button
                type="button"
                onClick={() => onBook({ kind: 'custom', themeId: null })}
                className="mt-auto inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#0F172A] bg-[#FFFFFF] font-semibold text-[#0F172A] hover:bg-[#F3F4F6]"
              >
                Describe it <ArrowRight className="h-4 w-4" />
              </button>
            </li>
          ) : null}
        </ul>
      </section>

      {/* Why they fit */}
      {data.fit || a.bio ? (
        <section className={`${C.card} order-4 p-4 sm:p-5 lg:order-none lg:col-start-1`}>
          {data.fit ? (
            <>
              <SectionTitle icon={Sparkles}>
                Why {first} fits {data.fit.campaignName ?? 'your brand'}
              </SectionTitle>
              {data.fit.parts?.length ? (
                <FitBreakdown fit={data.fit} />
              ) : data.fit.reasons.length ? (
                <ul className="-mt-1 space-y-1.5 text-sm text-[#374151]">
                  {data.fit.reasons.map((r) => (
                    <li key={r} className="flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#65A30D]" />
                      {r}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={`text-sm ${C.muted}`}>
                  Not much to match on yet: {first}'s audience data or{' '}
                  {data.fit.campaignName ? "this campaign's target" : "your brand's setup"} is
                  missing.
                </p>
              )}
            </>
          ) : null}
          {a.bio ? (
            <p className={`text-sm leading-relaxed text-[#374151] ${data.fit ? 'mt-4' : ''}`}>
              {a.bio}
            </p>
          ) : null}
        </section>
      ) : null}

      {/* Audience */}
      <section className={`${C.card} order-4 p-4 sm:p-5 lg:order-none lg:col-start-1`}>
        <SectionTitle
          icon={Users}
          right={
            data.audience ? (
              <span className={`text-xs ${C.muted}`}>
                From {platformOf(data.audience.source).label}
              </span>
            ) : null
          }
        >
          Who sees {first}'s content
        </SectionTitle>
        {notes.audience}
        {data.audience ? (
          <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_160px_minmax(0,1fr)]">
            <div className="min-w-0">
              <p className={`mb-2 text-sm font-semibold ${C.ink}`}>Age</p>
              <ul className="space-y-2.5">
                {data.audience.ages.map((x) => (
                  <li key={x.bucket} className="flex items-center gap-3 text-sm">
                    <span className={`w-12 shrink-0 ${C.ink}`}>{x.bucket.replace('-', '–')}</span>
                    <Bar share={x.share} max={maxAge} />
                    <span className={`w-10 shrink-0 text-right tabular-nums ${C.muted}`}>
                      {pct(x.share)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            {data.audience.genders.length ? <Donut split={data.audience.genders} /> : <div />}
            <div className="min-w-0">
              <p className={`mb-2 flex items-center gap-1.5 text-sm font-semibold ${C.ink}`}>
                <MapPin className="h-4 w-4" /> Top locations
              </p>
              <ul className="space-y-2.5">
                {data.audience.locations.map((l) => (
                  <li key={l.name} className="text-sm">
                    <div className="flex justify-between gap-3">
                      <span className={`truncate ${C.ink}`}>{l.name}</span>
                      <span className={`tabular-nums ${C.muted}`}>{pct(l.share)}</span>
                    </div>
                    <div className="mt-1 flex">
                      <Bar share={l.share} max={1} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <p className={`text-sm ${C.muted}`}>
            Audience data appears once {first} connects an account.
          </p>
        )}
      </section>
    </div>
  )
}

export default MarketplaceProfile
